/**
 * Enhanced debugging and logging utility for ParrotMate initialization
 * Provides detailed console logging, error tracking, and development tools
 */

class DebugLogger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isDebugMode = this.isDevelopment || localStorage.getItem('parrotmate_debug') === 'true';
    this.logLevel = this.getLogLevel();
    this.sessionId = this.generateSessionId();
    this.logs = [];
    this.maxLogHistory = 1000;
    
    // Initialize debug tools
    this.initializeDebugTools();
    
    // Log initialization
    this.info('DebugLogger initialized', {
      isDevelopment: this.isDevelopment,
      isDebugMode: this.isDebugMode,
      logLevel: this.logLevel,
      sessionId: this.sessionId
    });
  }

  /**
   * Generate unique session ID for debugging
   */
  generateSessionId() {
    return `debug-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get log level from environment or localStorage
   */
  getLogLevel() {
    const stored = localStorage.getItem('parrotmate_log_level');
    if (stored) return stored;
    
    return this.isDevelopment ? 'debug' : 'info';
  }

  /**
   * Set log level and persist to localStorage
   */
  setLogLevel(level) {
    const validLevels = ['debug', 'info', 'warn', 'error'];
    if (!validLevels.includes(level)) {
      this.warn('Invalid log level:', level);
      return;
    }
    
    this.logLevel = level;
    localStorage.setItem('parrotmate_log_level', level);
    this.info('Log level changed to:', level);
  }

  /**
   * Check if log level should be output
   */
  shouldLog(level) {
    const levels = { debug: 0, info: 1, warn: 2, error: 3 };
    return levels[level] >= levels[this.logLevel];
  }

  /**
   * Create structured log entry
   */
  createLogEntry(level, message, data = {}, context = {}) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      message,
      data,
      context: {
        sessionId: this.sessionId,
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...context
      },
      id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`
    };

    // Add to log history
    this.logs.push(logEntry);
    
    // Trim log history if too large
    if (this.logs.length > this.maxLogHistory) {
      this.logs = this.logs.slice(-this.maxLogHistory);
    }

    return logEntry;
  }

  /**
   * Debug level logging
   */
  debug(message, data = {}, context = {}) {
    if (!this.shouldLog('debug')) return null;
    
    const logEntry = this.createLogEntry('debug', message, data, context);
    
    if (this.isDebugMode) {
      console.debug(`[DEBUG] ${message}`, data, context);
    }
    
    return logEntry;
  }

  /**
   * Info level logging
   */
  info(message, data = {}, context = {}) {
    if (!this.shouldLog('info')) return null;
    
    const logEntry = this.createLogEntry('info', message, data, context);
    
    console.log(`[INFO] ${message}`, data);
    
    return logEntry;
  }

  /**
   * Warning level logging
   */
  warn(message, data = {}, context = {}) {
    if (!this.shouldLog('warn')) return null;
    
    const logEntry = this.createLogEntry('warn', message, data, context);
    
    console.warn(`[WARN] ${message}`, data);
    
    return logEntry;
  }

  /**
   * Error level logging
   */
  error(message, error = null, context = {}) {
    if (!this.shouldLog('error')) return null;
    
    const logEntry = this.createLogEntry('error', message, {
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
        status: error.status,
        code: error.code
      } : null
    }, context);
    
    console.error(`[ERROR] ${message}`, error, context);
    
    return logEntry;
  }

  /**
   * Log initialization step with enhanced timing and context
   */
  logInitializationStep(step, status, duration = null, metadata = {}) {
    const logData = {
      step,
      status,
      duration,
      timestamp: Date.now(),
      performanceCategory: duration ? this.categorizePerformance(duration) : null,
      cumulativeTime: metadata.cumulativeTime || null,
      progressPercentage: metadata.progressPercentage || null,
      ...metadata
    };

    const message = `Initialization Step: ${step} - ${status}${duration ? ` (${duration.toFixed(2)}ms)` : ''}`;
    
    // Enhanced logging with performance context
    let logEntry;
    if (status === 'started') {
      logEntry = this.debug(message, logData, { 
        category: 'initialization',
        subcategory: 'step_start',
        step,
        parrotId: metadata.parrotId
      });
    } else if (status === 'completed') {
      const level = logData.performanceCategory === 'slow' || logData.performanceCategory === 'critical' ? 'warn' : 'info';
      logEntry = this[level](message, logData, { 
        category: 'initialization',
        subcategory: 'step_complete',
        step,
        parrotId: metadata.parrotId,
        performance: logData.performanceCategory
      });
    } else if (status === 'failed') {
      logEntry = this.error(message, metadata.error, { 
        category: 'initialization',
        subcategory: 'step_error',
        step,
        duration,
        parrotId: metadata.parrotId,
        errorCode: metadata.errorCode
      });
    } else if (status === 'retrying') {
      logEntry = this.warn(message, logData, { 
        category: 'initialization',
        subcategory: 'step_retry',
        step,
        parrotId: metadata.parrotId,
        retryCount: metadata.retryCount
      });
    } else if (status === 'progress') {
      logEntry = this.debug(message, logData, { 
        category: 'initialization',
        subcategory: 'step_progress',
        step,
        parrotId: metadata.parrotId,
        progress: metadata.progressPercentage
      });
    }

    // Log performance warnings for slow steps
    if (duration && duration > 5000) {
      this.warn(`Slow initialization step detected: ${step} took ${duration.toFixed(2)}ms`, {
        step,
        duration,
        threshold: 5000,
        parrotId: metadata.parrotId
      }, {
        category: 'performance',
        subcategory: 'slow_initialization'
      });
    }

    // Return the log entry from the specific logging method, or create a fallback
    return logEntry || this.createLogEntry('info', message, logData, { 
      category: 'initialization',
      subcategory: status === 'started' ? 'step_start' : 
                   status === 'completed' ? 'step_complete' :
                   status === 'failed' ? 'step_error' :
                   status === 'retrying' ? 'step_retry' :
                   status === 'progress' ? 'step_progress' : 'step_unknown',
      step,
      parrotId: metadata.parrotId
    });
  }

  /**
   * Categorize performance based on duration
   */
  categorizePerformance(duration) {
    if (duration < 1000) return 'fast';
    if (duration < 3000) return 'normal';
    if (duration < 5000) return 'slow';
    return 'critical';
  }

  /**
   * Log initialization flow summary
   */
  logInitializationSummary(parrotId, totalDuration, steps = [], success = true, errorCode = null) {
    const summaryData = {
      parrotId,
      totalDuration,
      success,
      errorCode,
      stepCount: steps.length,
      steps: steps.map(step => ({
        name: step.name,
        duration: step.duration,
        status: step.status,
        performanceCategory: step.duration ? this.categorizePerformance(step.duration) : null
      })),
      performanceCategory: this.categorizePerformance(totalDuration),
      timestamp: Date.now()
    };

    const message = `Initialization ${success ? 'completed' : 'failed'} for parrot ${parrotId} in ${totalDuration.toFixed(2)}ms`;
    
    if (success) {
      const level = summaryData.performanceCategory === 'slow' || summaryData.performanceCategory === 'critical' ? 'warn' : 'info';
      this[level](message, summaryData, {
        category: 'initialization',
        subcategory: 'flow_summary',
        parrotId,
        performance: summaryData.performanceCategory
      });
    } else {
      this.error(message, null, {
        category: 'initialization',
        subcategory: 'flow_error',
        parrotId,
        errorCode,
        totalDuration
      });
    }

    // Log detailed step breakdown in debug mode
    if (this.isDebugMode && steps.length > 0) {
      console.group(`🔍 Initialization Steps Breakdown - ${parrotId}`);
      steps.forEach((step, index) => {
        const percentage = totalDuration > 0 ? ((step.duration / totalDuration) * 100).toFixed(1) : '0';
        console.log(`${index + 1}. ${step.name}: ${step.duration?.toFixed(2) || 'N/A'}ms (${percentage}%) - ${step.status}`);
      });
      console.groupEnd();
    }

    return summaryData;
  }

  /**
   * Log API request with detailed information
   */
  logApiRequest(requestId, method, endpoint, options = {}) {
    const logData = {
      requestId,
      method,
      endpoint,
      timeout: options.timeout,
      retryConfig: options.retryConfig,
      cacheEnabled: options.cacheEnabled,
      timestamp: Date.now()
    };

    this.debug(`API Request Started: ${method} ${endpoint}`, logData, { 
      category: 'api',
      requestId 
    });

    return logData;
  }

  /**
   * Log API response with timing and status
   */
  logApiResponse(requestId, status, duration, responseData = {}) {
    const logData = {
      requestId,
      status,
      duration,
      success: status >= 200 && status < 300,
      cacheHit: responseData.cacheHit || false,
      dataSize: responseData.dataSize || 0
    };

    const message = `API Response: ${status} (${duration}ms)`;
    
    if (logData.success) {
      this.info(message, logData, { category: 'api', requestId });
    } else {
      this.error(message, responseData.error, { category: 'api', requestId });
    }

    return logData;
  }

  /**
   * Log performance metrics
   */
  logPerformanceMetric(metric, value, context = {}) {
    const logData = {
      metric,
      value,
      unit: context.unit || 'ms',
      threshold: context.threshold,
      isSlowPerformance: context.threshold && value > context.threshold
    };

    const message = `Performance Metric: ${metric} = ${value}${logData.unit}`;
    
    if (logData.isSlowPerformance) {
      this.warn(message, logData, { category: 'performance' });
    } else {
      this.debug(message, logData, { category: 'performance' });
    }

    return logData;
  }

  /**
   * Log user action for debugging user flows
   */
  logUserAction(action, element = null, context = {}) {
    const logData = {
      action,
      element: element ? {
        tagName: element.tagName,
        id: element.id,
        className: element.className,
        textContent: element.textContent?.substring(0, 50)
      } : null,
      timestamp: Date.now(),
      ...context
    };

    this.debug(`User Action: ${action}`, logData, { category: 'user_interaction' });
    
    return logData;
  }

  /**
   * Log state change for debugging state management
   */
  logStateChange(component, previousState, newState, action = null) {
    const logData = {
      component,
      previousState: this.sanitizeState(previousState),
      newState: this.sanitizeState(newState),
      action,
      timestamp: Date.now()
    };

    this.debug(`State Change: ${component}`, logData, { category: 'state_management' });
    
    return logData;
  }

  /**
   * Sanitize state objects for logging (remove sensitive data)
   */
  sanitizeState(state) {
    if (!state || typeof state !== 'object') return state;
    
    const sanitized = { ...state };
    
    // Remove sensitive fields
    const sensitiveFields = ['password', 'token', 'apiKey', 'secret'];
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    return sanitized;
  }

  /**
   * Get logs filtered by criteria
   */
  getLogs(filters = {}) {
    let filteredLogs = [...this.logs];

    if (filters.level) {
      filteredLogs = filteredLogs.filter(log => log.level === filters.level);
    }

    if (filters.category) {
      filteredLogs = filteredLogs.filter(log => 
        log.context.category === filters.category
      );
    }

    if (filters.timeRange) {
      const cutoff = new Date(Date.now() - filters.timeRange).toISOString();
      filteredLogs = filteredLogs.filter(log => log.timestamp > cutoff);
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredLogs = filteredLogs.filter(log => 
        log.message.toLowerCase().includes(searchTerm) ||
        JSON.stringify(log.data).toLowerCase().includes(searchTerm)
      );
    }

    return filteredLogs;
  }

  /**
   * Export logs for debugging or support
   */
  exportLogs(filters = {}) {
    const logs = this.getLogs(filters);
    
    const exportData = {
      sessionId: this.sessionId,
      exportedAt: new Date().toISOString(),
      logLevel: this.logLevel,
      environment: {
        isDevelopment: this.isDevelopment,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: Date.now()
      },
      logs,
      summary: {
        totalLogs: logs.length,
        errorCount: logs.filter(l => l.level === 'error').length,
        warningCount: logs.filter(l => l.level === 'warn').length,
        categories: [...new Set(logs.map(l => l.context.category).filter(Boolean))]
      }
    };

    return exportData;
  }

  /**
   * Clear log history
   */
  clearLogs() {
    const logCount = this.logs.length;
    this.logs = [];
    this.info(`Cleared ${logCount} log entries`);
  }

  /**
   * Initialize debug tools for development
   */
  initializeDebugTools() {
    if (!this.isDebugMode) return;

    // Add debug tools to window for console access
    window.parrotmateDebug = {
      logger: this,
      setLogLevel: (level) => this.setLogLevel(level),
      getLogs: (filters) => this.getLogs(filters),
      exportLogs: (filters) => this.exportLogs(filters),
      clearLogs: () => this.clearLogs(),
      enableDebug: () => {
        localStorage.setItem('parrotmate_debug', 'true');
        this.isDebugMode = true;
        this.info('Debug mode enabled');
      },
      disableDebug: () => {
        localStorage.removeItem('parrotmate_debug');
        this.isDebugMode = false;
        this.info('Debug mode disabled');
      },
      showPerformanceStats: () => {
        const performanceLogs = this.getLogs({ category: 'performance' });
        console.table(performanceLogs.map(log => ({
          metric: log.data.metric,
          value: log.data.value,
          unit: log.data.unit,
          timestamp: log.timestamp
        })));
      },
      showApiStats: () => {
        const apiLogs = this.getLogs({ category: 'api' });
        const requests = apiLogs.filter(log => log.message.includes('Request Started'));
        const responses = apiLogs.filter(log => log.message.includes('Response'));
        
        console.log('API Statistics:', {
          totalRequests: requests.length,
          totalResponses: responses.length,
          averageResponseTime: responses.reduce((sum, log) => sum + (log.data.duration || 0), 0) / responses.length,
          errorRate: responses.filter(log => !log.data.success).length / responses.length * 100
        });
      }
    };

    // Log available debug commands
    this.info('Debug tools initialized. Available commands:', {
      commands: [
        'parrotmateDebug.setLogLevel(level)',
        'parrotmateDebug.getLogs(filters)',
        'parrotmateDebug.exportLogs()',
        'parrotmateDebug.clearLogs()',
        'parrotmateDebug.showPerformanceStats()',
        'parrotmateDebug.showApiStats()'
      ]
    });
  }

  /**
   * Create debug panel for visual debugging
   */
  createDebugPanel() {
    if (!this.isDebugMode || document.getElementById('parrotmate-debug-panel')) {
      return;
    }

    const panel = document.createElement('div');
    panel.id = 'parrotmate-debug-panel';
    panel.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      width: 300px;
      max-height: 400px;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      font-family: monospace;
      font-size: 12px;
      padding: 10px;
      border-radius: 5px;
      z-index: 10000;
      overflow-y: auto;
      display: none;
    `;

    const header = document.createElement('div');
    header.innerHTML = `
      <strong>ParrotMate Debug Panel</strong>
      <button onclick="this.parentElement.parentElement.style.display='none'" style="float: right; background: red; color: white; border: none; padding: 2px 6px; cursor: pointer;">×</button>
    `;
    panel.appendChild(header);

    const logContainer = document.createElement('div');
    logContainer.id = 'debug-log-container';
    panel.appendChild(logContainer);

    document.body.appendChild(panel);

    // Add keyboard shortcut to toggle panel
    document.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
        if (panel.style.display === 'block') {
          this.updateDebugPanel();
        }
      }
    });

    this.info('Debug panel created. Press Ctrl+Shift+D to toggle.');
  }

  /**
   * Update debug panel with recent logs
   */
  updateDebugPanel() {
    const container = document.getElementById('debug-log-container');
    if (!container) return;

    const recentLogs = this.getLogs({ timeRange: 5 * 60 * 1000 }); // Last 5 minutes
    
    container.innerHTML = recentLogs.slice(-20).map(log => `
      <div style="margin: 2px 0; padding: 2px; border-left: 3px solid ${this.getLogColor(log.level)};">
        <small>${new Date(log.timestamp).toLocaleTimeString()}</small>
        <strong>[${log.level.toUpperCase()}]</strong>
        ${log.message}
      </div>
    `).join('');
  }

  /**
   * Get color for log level
   */
  getLogColor(level) {
    const colors = {
      debug: '#888',
      info: '#4CAF50',
      warn: '#FF9800',
      error: '#F44336'
    };
    return colors[level] || '#888';
  }
}

// Create singleton instance
const debugLogger = new DebugLogger();

// Auto-update debug panel if it exists
if (debugLogger.isDebugMode) {
  setInterval(() => {
    if (document.getElementById('parrotmate-debug-panel')?.style.display === 'block') {
      debugLogger.updateDebugPanel();
    }
  }, 2000);
}

export default debugLogger;
